import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Appointment, ProcedureCode } from "@dentacore/shared-types";
import { isPlanOpen, PAYMENT_METHOD_LABELS, PAYMENT_METHODS } from "@dentacore/shared-types";

import type { TreatmentPlan } from "@dentacore/shared-types";

import { recordToothProcedure } from "../api/patients";
import { useCan } from "../auth/use-can";
import { formatMoney, parseMoney } from "../patients/money";
import type { VisitPayment } from "./appointment-payment";
import { linkPart, listPlans } from "../api/plans";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { Modal } from "../ui/modal";
import { Select } from "../ui/select";
import { QuickProcedure } from "./quick-procedure";
import { ToothPicker } from "./tooth-picker";
import styles from "./complete-visit-dialog.module.css";
import { procedureIds, visitMembers } from "./group-visits";
import { useBranchCurrency } from "../api/use-branch-currency";
import { useBookingScoped } from "./booking-location";

/**
 * Finishing a visit: **what was done, and on which tooth.**
 *
 * ── The gap this closes ─────────────────────────────────────────────────────
 *
 * An appointment names a procedure (`procedure_code_id`, TP18) and **never a
 * tooth** — there is no column for one, and there should not be: the person who
 * books is the front desk, and *"she is just someone that books the
 * appointment"*. The tooth is known by whoever does the work, at the moment
 * they finish.
 *
 * So completing a visit used to change a status and nothing else. The tooth
 * chart did not move, and because billing reads charted work
 * (`raiseForAppointment` looks for tooth procedures by `appointmentId`), no
 * invoice was raised either. The client reported both as separate faults; they
 * are one missing step.
 *
 * This is that step. It asks the clinician which tooth each procedure was done
 * on, charts it against **this appointment**, and lets the caller complete —
 * after which the chart, the patient's history and the billing tab all show the
 * work, because all three read the row this writes.
 *
 * ── What it deliberately does not do ────────────────────────────────────────
 *
 * **It invents no tooth.** A procedure whose catalogue row is not per-tooth
 * (a scale and polish, a consultation) is charted with no tooth at all, and one
 * that is per-tooth cannot be charted until somebody says which. Guessing "the
 * one they came in about" is the failure mode a clinical record cannot have.
 *
 * **A visit no longer finishes without saying what was done** (FX31, which
 * removed *Complete without charting*). A check-up is recorded as the check-up
 * it was — picked from the catalogue, or added here with *Not in the list?* —
 * so every completed visit has a procedure or a plan visit behind it. A held
 * chair, with no patient, still completes as before.
 *
 * **And it asks about money before it closes** (FX30): whether the patient paid
 * anything, all of it or part, and how. The caller records it against the
 * invoice the completion raises.
 */
/** What one patient's section hands the dialog: whether it is ready, and how to save it. */
interface VisitWorkApi {
  ready: boolean;
  /** Why it cannot be recorded yet, or null. */
  check: () => string | null;
  /** What it comes to, when that can be said exactly (FQ-06), else null. */
  cents: number | null;
  /** Chart its work against its own booking; answers its whole-mouth codes. */
  chart: () => Promise<string[]>;
}

export function CompleteVisitDialog({
  appointment,
  codes,
  busy,
  onCancel,
  onComplete,
}: {
  appointment: Appointment;
  /** This branch's catalogue, for the tooth question and for extra work. */
  codes: readonly ProcedureCode[] | null;
  busy: boolean;
  onCancel: () => void;
  /**
   * Charting is done; move the status, raise the invoices, and record the
   * payment chosen here, if any (FX30). Whole-mouth work is per booking, so a
   * family visit bills each child their own (FV-D5).
   */
  onComplete: (
    wholeMouth: { appointmentId: string; codeIds: string[] }[],
    payment: VisitPayment | null,
  ) => void;
}): React.JSX.Element {
  /**
   * **A family visit is one dialog for everyone** (FV-D5): a section per
   * patient — the same questions as for one, charted against that patient's
   * own booking — then one payment and one *Record and complete*.
   */
  const members = visitMembers(appointment);
  const family = members.length > 1;
  const hasPatient = members.some((member) => member.patient !== null);
  const can = useCan();
  const currency = useBranchCurrency();
  /** Only someone who may record payments is asked (PM-D3). */
  const asksPayment = hasPatient && can("billing.payment.record");
  const [paid, setPaid] = useState<"none" | "full" | "part">("none");
  const [paidAmount, setPaidAmount] = useState("");
  const [paidMethod, setPaidMethod] = useState<string>("CASH");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const sections = useRef(new Map<string, VisitWorkApi>());
  const [ready, setReady] = useState<Record<string, boolean>>({});
  const register = useCallback((id: string, api: VisitWorkApi): void => {
    sections.current.set(id, api);
    setReady((was) => (was[id] === api.ready ? was : { ...was, [id]: api.ready }));
  }, []);
  const allReady = members.every((member) => ready[member.id] === true);

  async function chartAndComplete(): Promise<void> {
    setError(null);
    const apis = members.map((member) => ({ member, api: sections.current.get(member.id) }));

    for (const { member, api } of apis) {
      const problem = api?.check() ?? null;
      if (problem !== null) {
        setError(family ? `${member.patient?.firstName ?? "One patient"}: ${problem}` : problem);
        return;
      }
    }

    // What the visit comes to, when every section can say exactly (FQ-06).
    const visitCents = apis.reduce<number | null>(
      (sum, { api }) =>
        sum === null || api === undefined || api.cents === null ? null : sum + api.cents,
      0,
    );

    let payment: VisitPayment | null = null;
    if (asksPayment && paid === "full") payment = { kind: "full", method: paidMethod };
    if (asksPayment && paid === "part") {
      const cents = parseMoney(paidAmount, currency);
      if (cents === null) {
        setError("Type the amount paid, above zero — or choose “Nothing yet”.");
        return;
      }
      if (visitCents !== null && visitCents > 0 && cents > visitCents) {
        setError(
          `That is more than this visit comes to (${formatMoney(visitCents, currency)}) — ` +
            "a part payment cannot be more. Nothing was completed.",
        );
        return;
      }
      payment = { kind: "part", amountCents: cents, method: paidMethod };
    }

    setSaving(true);
    try {
      const wholeMouth: { appointmentId: string; codeIds: string[] }[] = [];
      // One patient after another, never together — see `chart` below.
      for (const { member, api } of apis) {
        wholeMouth.push({ appointmentId: member.id, codeIds: (await api?.chart()) ?? [] });
      }
      onComplete(wholeMouth, payment);
    } catch (cause) {
      // **The visit is not completed when charting fails.** Half-charted work
      // followed by an invoice for it would be worse than neither.
      setError(cause instanceof Error ? cause.message : "Could not record that work.");
      setSaving(false);
    }
  }

  const working = busy || saving;

  return (
    <Modal label="Complete this visit" onClose={onCancel} testId="complete-visit">
      <div className={styles.dialog}>
        <h2 className={styles.title}>What was done at this visit?</h2>
        <p className={styles.lede}>
          {family
            ? "Each patient’s work goes onto their own tooth chart and their own bill, raised when the visit is completed."
            : `Whatever you record here goes onto ${appointment.patient?.firstName ?? "the patient"}’s tooth chart and onto the invoice raised when the visit is completed.`}
        </p>

        {members.map((member) => (
          <VisitWork
            key={member.id}
            appointment={member}
            codes={codes}
            working={working}
            named={family}
            register={register}
          />
        ))}

        {/*
          **Did they pay?** (FX30) — asked here, before the visit closes, rather
          than only on the panel afterwards. "Nothing yet" is a real answer: the
          invoice stays owed and the panel's *Record payment* takes it later.
        */}
        {asksPayment && (
          <fieldset className={styles.payment} data-testid="complete-payment">
            <legend className={styles.label}>Payment at this visit</legend>
            <div className={styles.paidChoice}>
              {(
                [
                  ["none", "Nothing yet"],
                  ["full", "Paid in full"],
                  ["part", "Paid part"],
                ] as const
              ).map(([value, text]) => (
                <label key={value} className={paid === value ? styles.paidOn : styles.paidOff}>
                  <input
                    type="radio"
                    name={`complete-paid-${appointment.id}`}
                    value={value}
                    checked={paid === value}
                    disabled={working}
                    onChange={() => setPaid(value)}
                    data-testid={`complete-paid-${value}`}
                  />
                  {text}
                </label>
              ))}
            </div>
            {paid !== "none" && (
              <div className={styles.paidFields}>
                {paid === "part" && (
                  <label className={styles.field}>
                    <span className={styles.label}>Amount, in {currency}</span>
                    <input
                      className={styles.input}
                      inputMode="decimal"
                      value={paidAmount}
                      disabled={working}
                      onChange={(event) => setPaidAmount(event.target.value)}
                      data-testid="complete-paid-amount"
                    />
                  </label>
                )}
                <label className={styles.field}>
                  <span className={styles.label}>Method</span>
                  <select
                    className={styles.input}
                    value={paidMethod}
                    disabled={working}
                    onChange={(event) => setPaidMethod(event.target.value)}
                    data-testid="complete-paid-method"
                  >
                    {PAYMENT_METHODS.map((name) => (
                      <option key={name} value={name}>
                        {PAYMENT_METHOD_LABELS[name]}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
            {paid === "full" && (
              <span className={styles.paidNote}>
                {family
                  ? "Whatever the bills for this visit come to is recorded as paid, first patient first."
                  : "Whatever the invoice for this visit comes to is recorded as paid."}
              </span>
            )}
          </fieldset>
        )}

        {error !== null && (
          <p className={styles.error} role="alert" data-testid="complete-error">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primary}
            // A held chair has nothing to seed; a visit waits for its lines.
            disabled={working || !allReady}
            onClick={() => void chartAndComplete()}
            data-testid="complete-confirm"
          >
            {saving ? "Recording…" : "Record and complete"}
          </button>
          <button type="button" className={styles.ghost} disabled={working} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * **One patient's work at this visit** — the plan question, the procedure lines
 * and their teeth, charted against that patient's own booking. Drawn once for
 * an ordinary visit and once per patient for a family (FV-D5).
 */
function VisitWork({
  appointment,
  codes,
  working,
  named,
  register,
}: {
  appointment: Appointment;
  codes: readonly ProcedureCode[] | null;
  working: boolean;
  /** A family: say whose section this is. */
  named: boolean;
  register: (id: string, api: VisitWorkApi) => void;
}): React.JSX.Element {
  const here = useBookingScoped();
  const patientId = appointment.patient?.id ?? null;
  const can = useCan();
  /**
   * A procedure added from inside this dialog (item 20).
   *
   * Held here rather than re-fetching the catalogue: the row is already in hand,
   * a re-fetch would race the line that is about to select it, and the caller's
   * `codes` prop belongs to the panel above.
   */
  const [added, setAdded] = useState<ProcedureCode[]>([]);

  /** Which line, if any, is currently asking for a new procedure. */
  const [addingFor, setAddingFor] = useState<string | null>(null);

  const pickable = useMemo(
    () => [...(codes ?? []).filter((code) => code.isActive), ...added],
    [codes, added],
  );

  /**
   * The procedures this visit was booked for, resolved to their catalogue rows
   * — the first, and any after it (FV-D6).
   *
   * A booking may name none — it is optional, and a check-up often does — in
   * which case this dialog starts empty and the clinician adds what was
   * actually done.
   */
  const bookedIds = procedureIds(appointment).join(",");
  const booked = useMemo(
    () => bookedIds.split(",").flatMap((id) => pickable.find((code) => code.id === id) ?? []),
    [pickable, bookedIds],
  );

  const [lines, setLines] = useState<Line[]>([]);
  /** The line whose × was pressed, waiting on "Remove it?". */
  const [removing, setRemoving] = useState<string | null>(null);
  /**
   * **Whether the lines have been filled in yet** (2026-10-16). Since FX31 there
   * is no other way to complete, so a *Record and complete* pressed in the
   * moment before the plan or booked procedure lands found no line and was
   * refused — the visit stayed in the chair. The button waits for this.
   */
  const [seeded, setSeeded] = useState(false);
  const [plans, setPlans] = useState<TreatmentPlan[] | null>(null);

  /**
   * The plan visit chosen *here*, when the appointment was not already linked
   * to one.
   *
   * **This is the second of the two dropdowns the client asked for** (item 20):
   * *"i should have 2 dropdowns, procedure and treatment plan … to record and
   * complete the appointment i should have 1 of the 2 selected."* The first
   * version of this dialog only ever *displayed* a plan the booking already
   * named, so a visit that turned out to be part of a plan could not be said
   * to be one at the moment anybody knew.
   */
  const [chosenVisit, setChosenVisit] = useState("");

  useEffect(() => {
    if (patientId === null) return;

    const controller = new AbortController();
    here(listPlans)(patientId, controller.signal)
      .then(setPlans)
      .catch((cause: unknown) => {
        /**
         * **An abort is not a failure, and treating it as one cost an hour.**
         *
         * React's development double-mount fires this effect twice and aborts
         * the first request. Resolving that abort to "no plans" let the seeding
         * below run with an empty list, write a blank line, and then refuse to
         * be replaced when the real response arrived a moment later — so a
         * visit that *is* part of a plan opened with the banner saying its
         * steps were filled in, above a line reading "Pick one…".
         *
         * A genuine failure still resolves to none, because the seeding waits
         * for this to settle and would otherwise leave the dialog with no lines
         * at all.
         */
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setPlans([]);
      });

    return () => controller.abort();
  }, [patientId]);

  /**
   * The plan visit this appointment is booked for, if any (decision D6).
   *
   * Found by walking the patient's plans, because the link lives on the plan's
   * steps rather than on the appointment — see `PlanLink`.
   */
  const planVisit = useMemo(() => {
    for (const plan of plans ?? []) {
      for (const [index, part] of plan.parts.entries()) {
        if (part.items.some((item) => item.appointmentId === appointment.id)) {
          return { plan, part, number: index + 1 };
        }
      }
    }
    return null;
  }, [plans, appointment.id]);

  /**
   * Every visit that could be this one — each live plan's next unfinished
   * visit, which is the only one the plan's own order rule (TP8) would accept.
   */
  const visitOptions = useMemo(() => {
    const rows: { value: string; label: string; keywords: string }[] = [];

    for (const plan of plans ?? []) {
      /**
       * **Open means not finished and not abandoned** (TP29, TP31).
       *
       * This used to read `status !== "IN_PROGRESS"`, against a column nothing
       * ever advanced — so it matched every plan ever created, and a patient
       * whose crown was finished last month was still offered it here.
       */
      if (!isPlanOpen(plan)) continue;

      const index = plan.parts.findIndex(
        (part) => !part.items.every((item) => item.status === "COMPLETED"),
      );
      const part = index === -1 ? undefined : plan.parts[index];
      if (part === undefined) continue;

      rows.push({
        value: `${plan.id}:${part.id}`,
        label: `${plan.title ?? "Treatment plan"} — visit ${String(index + 1)}`,
        keywords: part.items.map((item) => item.label ?? "").join(" "),
      });
    }

    return rows;
  }, [plans]);

  /** The visit in force: the one this booking is linked to, or the one picked. */
  const activeVisit = useMemo(() => {
    if (planVisit !== null) return planVisit;
    if (chosenVisit === "") return null;

    const [planId, partId] = chosenVisit.split(":");
    for (const plan of plans ?? []) {
      if (plan.id !== planId) continue;
      const index = plan.parts.findIndex((part) => part.id === partId);
      const part = index === -1 ? undefined : plan.parts[index];
      if (part !== undefined) return { plan, part, number: index + 1 };
    }
    return null;
  }, [planVisit, chosenVisit, plans]);

  /**
   * **Seeding the dialog, in the order the client asked for** (item 20).
   *
   * *"in case i have selected a treatment plan it should automatically be
   * selected … in case i dont have a treatment plan for this visit it should be
   * empty and i should be able to select a procedure."*
   *
   *   1. **A plan visit** fills a line per step, each with the tooth the plan
   *      already names — that is the whole value of having planned it, and
   *      retyping it here is how a plan and a chart come to disagree.
   *   2. **The booked procedure**, when there is no plan.
   *   3. **One empty line regardless**, which is the fix for a separate report:
   *      *"upon completing a visit the procedure added was not reflected in the
   *      tooth"*. A booking with no catalogue procedure used to open this dialog
   *      with **no rows at all**, so there was nothing to fill in and nothing was
   *      ever charted — the visit completed and the tooth stayed sound.
   */
  useEffect(() => {
    /**
     * **Nothing is seeded until the plans have resolved** (found in the audit,
     * 2026-09-06).
     *
     * The three sources are tried in order and the first to arrive wins, since
     * a line already chosen must never be overwritten under somebody's cursor.
     * Plans are fetched and the blank fallback is not, so the fallback always
     * won: the banner said *"its steps are filled in below"* while the only
     * line read "Pick one…".
     *
     * Waiting costs a moment on a dialog that is already loading its
     * catalogue, and it is the only way the ordering can mean anything.
     */
    if (plans === null) return;

    setLines((was) => {
      if (was.length > 0) return was;

      if (activeVisit !== null) {
        const fromPlan = activeVisit.part.items
          .filter((item) => item.status !== "COMPLETED")
          .map((item, index) => ({
            key: `plan-${item.id}`,
            codeId: item.procedureCodeId ?? "",
            tooth: item.fdiNumber === null ? "" : String(item.fdiNumber),
            fromPlan: true,
            planItemId: item.id,
            fallbackLabel: item.label ?? `Step ${String(index + 1)}`,
          }));
        if (fromPlan.length > 0) return fromPlan;
      }

      // The booked procedures — the first **one line per booked tooth** (BF3,
      // BF6), so the natural path charts the work where the chart already shows
      // it red; each further one a line of its own, its tooth asked here (FV-D19).
      const [first, ...more] = booked;
      if (first !== undefined) {
        const firstLines =
          appointment.fdiNumbers.length === 0
            ? [{ key: "booked", codeId: first.id, tooth: "" }]
            : appointment.fdiNumbers.map((fdi) => ({
                key: `booked-${String(fdi)}`,
                codeId: first.id,
                tooth: String(fdi),
              }));
        return [
          ...firstLines,
          ...more.map((code) => ({ key: `booked-${code.id}`, codeId: code.id, tooth: "" })),
        ];
      }

      // The empty row. Never no rows.
      return [{ key: "blank", codeId: "", tooth: "" }];
    });
    setSeeded(true);
  }, [booked, activeVisit, plans]);

  /**
   * What this line may be set to.
   *
   * ── Why it is not simply the catalogue ──────────────────────────────────
   *
   * A plan follows the patient across a clinic's branches and a catalogue
   * belongs to one branch (ADR-0008), so a plan step can perfectly well name a
   * procedure **this** branch does not have. A retired code does the same, since
   * the picker offers active ones only.
   *
   * Either way the seeded line pointed at an id the dropdown did not contain,
   * so it rendered as "Pick one…" with no tooth field — a step the plan says is
   * happening today that the clinician could not record. Found in the audit,
   * against a plan whose whitening was both retired *and* another branch's.
   *
   * So a line carries its own step as an option when the catalogue is missing
   * it. Charting accepts the id regardless — a tooth procedure references a
   * code, and the plan already decided which one.
   */
  const optionsFor = (line: Line): { value: string; label: string }[] => {
    const base = pickable.map((option) => ({ value: option.id, label: option.label }));
    if (line.codeId === "" || base.some((option) => option.value === line.codeId)) return base;

    return [
      {
        value: line.codeId,
        // Named as the plan named it, and said to be from there, so nobody
        // wonders why it is not in the list they can otherwise search.
        label: `${line.fallbackLabel ?? "This step"} — from the plan`,
      },
      ...base,
    ];
  };

  const codeOf = (id: string): ProcedureCode | null =>
    pickable.find((code) => code.id === id) ?? null;

  /**
   * Whether this line has to name a tooth.
   *
   * The catalogue decides when it knows the procedure (TP12). When it does not
   * — a plan step from another branch, or a retired code — the plan's own tooth
   * is the answer, and a line seeded with one still needs it.
   */
  const needsTooth = (line: Line): boolean => {
    const code = codeOf(line.codeId);
    if (code !== null) return code.scope === "PER_TOOTH";
    return line.fromPlan === true && line.tooth !== "";
  };
  const incomplete = lines.filter(
    (line) => line.codeId !== "" && needsTooth(line) && line.tooth === "",
  );

  /**
   * Whole-mouth work, billed rather than charted.
   *
   * **Charting here writes a `tooth_procedure`, and that row requires a tooth**
   * — `fdi_number` is `NOT NULL`, correctly, because a scale and polish is not
   * a fact about tooth 26. So an examination or a hygiene visit has nowhere to
   * go, and the dialog used to post `Number("")` — zero — and get *"0 is not an
   * FDI tooth number"* back, which names neither the cause nor a way forward.
   *
   * These lines therefore skip charting and travel with the completion request
   * instead, where they are priced from this branch's catalogue and become
   * invoice lines with no tooth. Two of the five procedures in the starter
   * library are whole mouth, so this is the commonest visit a clinic has.
   */
  const wholeMouth = lines.filter((line) => {
    const code = codeOf(line.codeId);
    return code !== null && code.scope === "WHOLE_MOUTH";
  });

  /**
   * **What an ordinary visit comes to here**, so a part payment above it is
   * stopped before the visit is completed rather than after (FQ-06). Only when
   * it can be said exactly — every line a code from this catalogue, priced once
   * per line as the server bills it. A plan visit's steps carry prices copied
   * when it was booked, and a multi-visit procedure bills one visit's price, so
   * those return null and the server's own refusal after closing still stands.
   */
  const visitCents = ((): number | null => {
    if (activeVisit !== null) return null;
    let sum = 0;
    for (const line of lines.filter((row) => row.codeId !== "")) {
      const code = codeOf(line.codeId);
      if (code === null || code.visits.length > 0) return null;
      sum += code.defaultPriceCents;
    }
    return sum > 0 ? sum : null;
  })();

  /** Why this section cannot be recorded yet, or null. */
  const check = (): string | null => {
    if (patientId === null) return null;
    const chartable = lines.filter((line) => line.codeId !== "");
    /**
     * **One of the two, which is exactly what was asked for** (item 20):
     * *"to record and complete the appointment i should have 1 of the 2
     * selected, treatment plan and procedure."*
     *
     * Since FX31 there is no other way to complete, so a check-up is
     * recorded as one too.
     */
    if (chartable.length === 0 && activeVisit === null) {
      return (
        "Say which procedure was done, or choose the treatment plan this visit belongs to. " +
        "A check-up is a procedure too — add it with “Not in the list?” if it is missing."
      );
    }
    if (incomplete.length > 0) {
      return "Say which tooth each procedure was done on, or remove the line.";
    }
    return null;
  };

  /** Chart this patient's work against their own booking; answers its whole-mouth codes. */
  const chart = async (): Promise<string[]> => {
    if (patientId === null) return [];
    const chartable = lines.filter((line) => line.codeId !== "");
    /**
     * **A plan visit's own steps are charted and billed by the server**, when
     * the appointment completes (TP27, `PlanCompletionService`). Charting them
     * here as well wrote the tooth twice and raised a second invoice — the path
     * nobody took while *Complete without charting* existed for plan visits,
     * and the only path since FX31. So only what was added at the chair goes.
     */
    const added = chartable.filter((line) => line.fromPlan !== true);
    // Named on the completion request; everything else is charted per tooth.
    const wholeMouthCodeIds = wholeMouth
      .filter((line) => line.fromPlan !== true)
      .map((line) => line.codeId);
    const perTooth = added.filter((line) => !wholeMouthCodeIds.includes(line.codeId));

    // **Sequential, not `Promise.all`.** Each write re-derives the tooth's
    // state in its own transaction, and two landing together on the same
    // tooth is exactly the race that produces a chart disagreeing with its
    // own record.
    for (const line of perTooth) {
      /**
       * **The line's own id, not the catalogue's** (found in the audit,
       * 2026-09-06): a plan step naming a retired procedure, or one belonging
       * to another branch, matched nothing in this branch's active catalogue
       * and was **skipped in silence** — the visit completed, the tooth stayed
       * sound, and no invoice was raised. The id is what the line already
       * holds; the catalogue is consulted for a nicer label and nothing else.
       */
      const code = codeOf(line.codeId);
      const label = code?.label ?? line.fallbackLabel ?? "Procedure";

      await here(recordToothProcedure)(patientId, Number(line.tooth), {
        label,
        procedureCodeId: line.codeId,
        // The link that makes it billable, and the whole point of charting
        // here rather than on the chart tab.
        appointmentId: appointment.id,
      });
    }
    /**
     * **A plan chosen here is attached before the visit closes.**
     *
     * Otherwise the plan would still show this visit as waiting to be booked
     * while the appointment it actually happened at was already complete —
     * the two records disagreeing about the same afternoon.
     */
    if (planVisit === null && activeVisit !== null) {
      await here(linkPart)(activeVisit.plan.id, activeVisit.part.id, appointment.id);
    }
    return wholeMouthCodeIds;
  };

  // Handed up on every render, so the dialog always saves what is on screen.
  useEffect(() => {
    register(appointment.id, {
      ready: patientId === null || seeded,
      check,
      cents: visitCents,
      chart,
    });
  });

  return (
    <section
      className={named ? styles.person : undefined}
      aria-label={
        appointment.patient === null
          ? "Held chair"
          : `${appointment.patient.firstName} ${appointment.patient.lastName}`
      }
      data-testid="complete-person"
    >
      {named && appointment.patient !== null && (
        <h3 className={styles.personName}>
          {appointment.patient.firstName} {appointment.patient.lastName}
        </h3>
      )}
      {/*
        **The treatment-plan half of the two dropdowns** (item 20).

        Already linked, and it reads as a statement — the booking said which
        visit this is and there is nothing to choose. Not linked, and it is a
        question, because the moment somebody knows this visit belonged to a
        plan is usually now rather than when the front desk booked it.
      */}
      {/* Attaching it to a plan is `plans.edit` (QA third run). */}
      {patientId !== null && planVisit === null && visitOptions.length > 0 && can("plans.edit") && (
        <div className={styles.planPick}>
          <Select
            id={`complete-plan-${appointment.id}`}
            label="Treatment plan"
            value={chosenVisit}
            options={visitOptions}
            onChange={setChosenVisit}
            testId="complete-plan"
            placeholder="Not part of a plan"
            emptyLabel="Not part of a plan"
            disabled={working}
            note="Choose a plan visit, or record the procedures below. Either will do."
          />
        </div>
      )}

      {planVisit !== null && (
        <p className={styles.planned} data-testid="complete-plan">
          Part of <strong>{planVisit.plan.title}</strong> &mdash; visit {planVisit.number}
          {planVisit.part.title === null ? "" : ` · ${planVisit.part.title}`}. Its steps are filled
          in below, with the teeth the plan names.
        </p>
      )}

      {patientId === null ? (
        <p className={styles.note}>
          This is a held chair rather than a patient&rsquo;s visit, so there is nothing to chart and
          nothing to bill.
        </p>
      ) : (
        <>
          <ul className={styles.lines}>
            {lines.map((line, index) => {
              const code = codeOf(line.codeId);
              return (
                <li key={line.key} className={styles.line}>
                  <div className={styles.field}>
                    <Select
                      id={`complete-procedure-${line.key}`}
                      label="Procedure"
                      required
                      value={line.codeId}
                      options={optionsFor(line)}
                      onChange={(codeId) => {
                        setLines((was) =>
                          was.map((row, i) =>
                            // The tooth belongs to the procedure that was
                            // chosen, not to the row — keeping it would carry
                            // one tooth's number onto a whitening.
                            i === index ? { ...row, codeId, tooth: "" } : row,
                          ),
                        );
                      }}
                      testId="complete-procedure"
                      placeholder={
                        line.fromPlan === true && line.codeId === ""
                          ? `${line.fallbackLabel ?? "This step"} — not in this catalogue`
                          : "Pick one…"
                      }
                      disabled={working}
                    />

                    {/*
                      **The way out of "it is not in the list"** (item 20).
                      Without it the choice was to abandon the dialog and walk
                      to Settings, or to complete without charting and lose
                      the tooth, the history and the invoice.
                    */}
                    {addingFor !== line.key && line.codeId === "" && (
                      <button
                        type="button"
                        className={styles.linklike}
                        disabled={working}
                        onClick={() => setAddingFor(line.key)}
                        data-testid="complete-add-procedure"
                      >
                        Not in the list? Add it
                      </button>
                    )}

                    {addingFor === line.key && (
                      <QuickProcedure
                        onCreated={(code) => {
                          setAdded((was) => [...was, code]);
                          setLines((was) =>
                            was.map((row, i) =>
                              i === index ? { ...row, codeId: code.id, tooth: "" } : row,
                            ),
                          );
                          setAddingFor(null);
                        }}
                        onCancel={() => setAddingFor(null)}
                      />
                    )}
                  </div>

                  {/*
                    Only where the catalogue says the procedure is done on a
                    tooth (TP12). Drawn as absent rather than disabled: a
                    greyed-out tooth box beside a whitening reads as something
                    the clinician failed to fill in.
                  */}
                  {(code?.scope === "PER_TOOTH" ||
                    (line.fromPlan === true && line.tooth !== "")) && (
                    <div className={styles.toothField}>
                      {/*
                        **On the patient's chart, not a list of 32 numbers**
                        (Emile's QA, 2026-10-18), the picker the booking form
                        already uses — and one label, where the list printed
                        "Tooth" twice (this wrapper's and its own).
                      */}
                      <ToothPicker
                        patientId={patientId}
                        single
                        required
                        label="Tooth"
                        value={line.tooth === "" ? [] : [Number(line.tooth)]}
                        onChange={(teeth) => {
                          const tooth = teeth[0] === undefined ? "" : String(teeth[0]);
                          setLines((was) =>
                            was.map((row, i) => (i === index ? { ...row, tooth } : row)),
                          );
                        }}
                        testId="complete-tooth"
                        disabled={working}
                      />
                    </div>
                  )}

                  <button
                    type="button"
                    className={styles.remove}
                    disabled={working}
                    aria-label="Remove this line"
                    // Asked first (Emile's QA, 2026-10-18): a line can carry a
                    // plan step or a tooth already chosen.
                    onClick={() => setRemoving(line.key)}
                    data-testid="complete-remove-line"
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>

          {removing !== null && (
            <ConfirmDialog
              title="Remove this procedure?"
              confirmLabel="Remove"
              tone="danger"
              busy={false}
              onCancel={() => setRemoving(null)}
              onConfirm={() => {
                setLines((was) => was.filter((row) => row.key !== removing));
                setRemoving(null);
              }}
              testId="complete-remove-confirm"
            >
              <p>
                {(() => {
                  const line = lines.find((row) => row.key === removing);
                  const name = line === undefined ? undefined : codeOf(line.codeId)?.label;
                  const tooth =
                    line === undefined || line.tooth === "" ? "" : ` on tooth ${line.tooth}`;
                  return name === undefined
                    ? "This line comes off the visit. Nothing is charted or billed for it."
                    : `${name}${tooth} comes off the visit. Nothing is charted or billed for it.`;
                })()}
              </p>
            </ConfirmDialog>
          )}

          {pickable.length === 0 ? (
            <p className={styles.note}>
              This location has no procedures in its catalogue yet, so there is nothing to chart
              against. The visit can still be completed.
            </p>
          ) : (
            <button
              type="button"
              className={styles.ghost}
              disabled={working}
              onClick={() =>
                setLines((was) => [
                  ...was,
                  {
                    key: `extra-${String(was.length)}-${String(Date.now())}`,
                    codeId: "",
                    tooth: "",
                  },
                ])
              }
              data-testid="complete-add-line"
            >
              Add something else that was done
            </button>
          )}
        </>
      )}
    </section>
  );
}

interface Line {
  /** Stable across re-renders, so removing a middle row does not reshuffle. */
  key: string;
  codeId: string;
  tooth: string;
  /** Seeded from a plan's step rather than chosen here (decision D6). */
  fromPlan?: boolean;
  /** The plan step this row came from, for the label below when it has no code. */
  planItemId?: string;
  /**
   * What the plan called this step, shown when its procedure is not in **this**
   * location's catalogue.
   *
   * A plan follows the patient across a clinic's branches and a catalogue
   * belongs to one branch (ADR-0008), so a crown prepped at one location and
   * cemented at another genuinely names a code that is not here. Saying so
   * beats an empty dropdown with no explanation.
   */
  fallbackLabel?: string;
}
